import type { ColoredMaterial } from "@color-finder/color-finder-api/models";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import ColorStatusDisplay from "components/dataDisplay/ColorStatusDisplay";
import { DeltaEDisplay } from "components/dataDisplay/DeltaEDisplay";
import { OpacityDisplay } from "components/dataDisplay/OpacityDisplay";
import { ColoredMaterialDetailsDialog } from "components/dialog/ColoredMaterialDetailsDialog";
import { DigitalSampleDownloadDialog } from "components/digitalSamples/DigitalSampleDownloadDialog";
import type { NamedColor } from "components/filter/color-filter/ColorFilter";
import { useDigitalSample } from "hooks/digital-samples";
import { usePreviewSpecification } from "hooks/specifications";
import React, { type FC } from "react";
import { useTranslation } from "react-i18next";
import { CollectionBookmarkButton } from "../collection-bookmark/CollectionBookmarkButton";
import { ColoredMaterialPreview } from "../preview/ColoredMaterialPreview";

export interface SearchResultsGridTileProps {
  material: ColoredMaterial;
  isInternal: boolean;
  target?: NamedColor;
  imageType?: "THUMBNAIL" | "SQUARE";
  imageAspectRatio: string;
  onMaterialSelect?: (material: ColoredMaterial) => void;
}

const gridView = "GRID";

export const SearchResultsGridTile: FC<SearchResultsGridTileProps> = ({
  material,
  isInternal,
  target,
  imageAspectRatio,
  imageType,
  onMaterialSelect,
}) => {
  const [digitalSamples] = useDigitalSample(
    { eljExperimentId: material.eljExperimentId },
    material.digitalSample.hasDigitalSample,
  );
  const previewColor = usePreviewSpecification(material);
  const previewColorValue = previewColor?.value?.[0];
  const labelColor =
    (previewColorValue && previewColorValue > 60) ||
    (imageType === "SQUARE" && material.digitalSample.hasDigitalSample)
      ? undefined
      : "rgba(255, 255, 255, 0.8)";

  const [openDetail, setOpenDetail] = React.useState(false);
  const [openDownloadDialog, setOpenDownloadDialog] = React.useState(false);
  const [isPreviewImage, setIsPreviewImage] = React.useState(false);

  const handelOpenDetail = () => {
    setOpenDetail(true);
    onMaterialSelect?.(material);
  };
  const handleCloseDetail = () => setOpenDetail(false);

  const handleOpenDownloadDialog = () => setOpenDownloadDialog(true);
  const handleCloseDownloadDialog = () => setOpenDownloadDialog(false);

  const handlePreview = () => setIsPreviewImage(true);
  const handleClosePreview = () => setIsPreviewImage(false);

  const { t } = useTranslation();

  return (
    <Paper sx={{ height: "100%" }}>
      <Stack height="100%">
        <Box
          position="relative"
          sx={{ width: "100%", aspectRatio: imageAspectRatio }}
          borderBottom={(theme) => `1px solid ${theme.palette.divider}`}
        >
          <ColoredMaterialPreview
            sx={{ borderLeft: 0, borderRight: 0, borderTop: 0 }}
            display="flex"
            width="100%"
            height="100%"
            imageType={imageType}
            viewType={gridView}
            material={material}
            target={target}
            isOpenPreviewImage={isPreviewImage}
            onClosePreview={() => handleClosePreview()}
            onOpenDetail={handelOpenDetail}
          />
          <Stack
            direction="row"
            gap="var(--spacing4, 4px)"
            sx={{
              position: "absolute",
              bottom: (theme) => theme.spacing(1),
              right: (theme) => theme.spacing(1),
            }}
          >
            {material.digitalSample.hasDigitalSample && (
              <Button
                title={t("src_result_grid_preview")}
                color="primary"
                size="small"
                onClick={handlePreview}
                sx={{
                  boxShadow: (theme) => theme.shadows[3],
                  color: "#000",
                  backgroundColor: "#fff",
                  height: "24px",
                  minWidth: "24px",
                  borderRadius: "2px",
                  padding: "0",
                  border: "1px solid #d9d9d9",
                  "&:hover": {
                    backgroundColor: "#d9d9d9",
                    color: "#000",
                  },
                }}
              >
                <VisibilityIcon
                  fontSize="small"
                  sx={{ margin: "auto", width: "14px", height: "14px" }}
                />
              </Button>
            )}
            <CollectionBookmarkButton
              eljExperimentId={material.eljExperimentId}
            />
            {material.digitalSample.hasDigitalSample && (
              <Button
                title={t("src_result_grid_download")}
                color="primary"
                size="small"
                onClick={handleOpenDownloadDialog}
                sx={{
                  boxShadow: (theme) => theme.shadows[3],
                  color: "#009FE4",
                  backgroundColor: "#fff",
                  height: "24px",
                  minWidth: "24px",
                  borderRadius: "2px",
                  padding: "0",
                  border: "1px solid #d9d9d9",
                  "&:hover": {
                    backgroundColor: "#d9d9d9",
                  },
                }}
              >
                <DownloadIcon
                  fontSize="small"
                  sx={{ margin: "auto", width: "14px", height: "14px" }}
                />
              </Button>
            )}
          </Stack>

          {target && previewColor && (
            <DeltaEDisplay
              preview={previewColor}
              target={target}
              sx={{
                position: "absolute",
                color: labelColor,
                top: (theme) => theme.spacing(1),
                left: (theme) => theme.spacing(1),
              }}
            />
          )}
        </Box>
        <Stack flexGrow={1} padding={"12px"} justifyContent="space-between">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Typography
              mr={1}
              fontSize="var(--body-b3-12-regular-size, 12px)"
              fontWeight="500"
            >
              {material.material.publicGradeName}
            </Typography>
            <OpacityDisplay fontSize="small" value={material.color.opacity} />
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              fontSize="var(--body-b3-12-regular-size, 12px)"
              fontFamily='var(--font-family, "AktivGrotesk")'
              fontWeight={500}
            >
              {material.color.finalColorCode || ""}
            </Typography>
            {isInternal && (
              <ColorStatusDisplay value={material.availability.colorStatus} />
            )}
          </Stack>
        </Stack>
      </Stack>
      <ColoredMaterialDetailsDialog
        open={openDetail}
        onClose={handleCloseDetail}
        material={material}
        isInternal={isInternal}
      />
      {digitalSamples?.[0] && (
        <DigitalSampleDownloadDialog
          open={openDownloadDialog}
          onClose={handleCloseDownloadDialog}
          digitalSample={digitalSamples[0]}
          material={material}
        />
      )}
    </Paper>
  );
};
